import type { MarginInventoryLatestResponse, MarginMarketResponse } from "@yutai/shared"
import { useMemo, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import useSWR from "swr"
import { useAllBenefits } from "../../lib/useAllBenefits.js"
import { MarginInventoryDiffPanel } from "./MarginInventoryDiffPanel.js"
import styles from "./MarginInventoryPage.module.css"
import { MarginInventoryRowCard } from "./MarginInventoryRowCard.js"
import { MarginInventoryUploadPanel } from "./MarginInventoryUploadPanel.js"
import type { BenefitValueOverrides } from "./benefitOptions.js"
import { DEFAULT_LOAN_RATE, LOAN_RATE_TIERS } from "./crossCost.js"
import { adminFetch } from "./marginInventoryApi.js"
import { buildThisMonthInventoryRows, thisMonthBenefits } from "./thisMonthInventory.js"

const MONTH_TAB_LABELS = ["今月", "来月", "再来月"]

function formatShortDate(date: string | undefined): string {
  if (!date) return "未登録"
  const [, m, d] = date.split("-")
  return `${Number(m)}/${Number(d)}`
}

const LOAN_RATE_KEY = "marginInventory.loanRate"

function readLoanRate(): number {
  try {
    const saved = Number(localStorage.getItem(LOAN_RATE_KEY))
    if (LOAN_RATE_TIERS.some((t) => t.rate === saved)) return saved
  } catch {
    // localStorage が使えない環境ではデフォルト
  }
  return DEFAULT_LOAN_RATE
}

// 一般信用在庫（楽天）: CSVアップロードと、直近アップロードのうち今月・来月・再来月
// （タブで切替）が権利確定月の優待銘柄だけの在庫一覧（優待内容・管理者用メモつき）。無期限CSVは今月の優待と
// 無関係な銘柄も含むので、ここで絞る。
export function MarginInventoryPage() {
  const currentMonth = new Date().getMonth() + 1
  // タブの選択は URL（?month=N）に反映し、ブラウザバックで戻っても同じタブを開く。
  const [searchParams, setSearchParams] = useSearchParams()
  const paramMonth = Number(searchParams.get("month"))
  const paramOffset = (paramMonth - currentMonth + 12) % 12
  const monthOffset =
    paramMonth >= 1 && paramMonth <= 12 && paramOffset < MONTH_TAB_LABELS.length ? paramOffset : 0
  const setMonthOffset = (offset: number) => {
    const next = new URLSearchParams(searchParams)
    next.set("month", String(((currentMonth - 1 + offset) % 12) + 1))
    setSearchParams(next, { replace: true })
  }
  const month = ((currentMonth - 1 + monthOffset) % 12) + 1
  const { data: benefits, error: benefitsError, mutate: mutateBenefits } = useAllBenefits()
  const [inStockOnly, setInStockOnly] = useState(true)
  const [loanRate, setLoanRate] = useState(readLoanRate)
  const today = new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10)

  const monthBenefits = useMemo(
    () => (benefits ? thisMonthBenefits(benefits, month) : []),
    [benefits, month]
  )
  const codes = useMemo(() => monthBenefits.map((b) => b.id).sort(), [monthBenefits])

  // タブに件数を出すため、在庫は3タブ分の銘柄をまとめて取得する
  const tabBenefits = useMemo(
    () =>
      MONTH_TAB_LABELS.map((_, offset) =>
        benefits ? thisMonthBenefits(benefits, ((currentMonth - 1 + offset) % 12) + 1) : []
      ),
    [benefits, currentMonth]
  )
  const tabCodes = useMemo(
    () => [...new Set(tabBenefits.flat().map((b) => b.id))].sort(),
    [tabBenefits]
  )

  const {
    data: latest,
    error: latestError,
    mutate,
  } = useSWR<MarginInventoryLatestResponse>(
    tabCodes.length > 0 ? ["admin-margin-inventory-latest", tabCodes.join(",")] : null,
    () =>
      adminFetch("/margin-inventory/latest", {
        method: "POST",
        body: JSON.stringify({ codes: tabCodes }),
      }),
    { revalidateOnFocus: false }
  )

  const { data: market } = useSWR<MarginMarketResponse>(
    codes.length > 0 ? ["admin-margin-market", codes.join(",")] : null,
    () =>
      adminFetch("/margin-inventory/market", {
        method: "POST",
        body: JSON.stringify({ codes }),
      }),
    { revalidateOnFocus: false }
  )

  const { data: memos, mutate: mutateMemos } = useSWR<Record<string, string>>(
    "admin-margin-inventory-memos",
    () => adminFetch("/margin-inventory/memos"),
    { revalidateOnFocus: false }
  )

  const { data: valueOverrides, mutate: mutateValueOverrides } = useSWR<
    Record<string, BenefitValueOverrides>
  >("admin-margin-benefit-values", () => adminFetch("/margin-inventory/benefit-values"), {
    revalidateOnFocus: false,
  })

  async function saveBenefitValues(code: string, values: BenefitValueOverrides) {
    await adminFetch(`/margin-inventory/benefit-values/${encodeURIComponent(code)}`, {
      method: "PUT",
      body: JSON.stringify({ values }),
    })
    await mutateValueOverrides(
      (prev) => {
        const next = { ...(prev ?? {}) }
        if (Object.keys(values).length === 0) delete next[code]
        else next[code] = values
        return next
      },
      { revalidate: false }
    )
  }

  // クロールはバックグラウンドで走り、完了後に benefits.json が再公開される。少し待って読み直す。
  async function crawlCompany(code: string) {
    await adminFetch(`/companies/${encodeURIComponent(code)}/crawl`, { method: "POST" })
    window.setTimeout(() => mutateBenefits(), 20_000)
  }

  async function saveMemo(code: string, memo: string) {
    await adminFetch(`/margin-inventory/memos/${encodeURIComponent(code)}`, {
      method: "PUT",
      body: JSON.stringify({ memo }),
    })
    await mutateMemos(
      (prev) => {
        const next = { ...(prev ?? {}) }
        if (memo.trim() === "") delete next[code]
        else next[code] = memo
        return next
      },
      { revalidate: false }
    )
  }

  const rows = useMemo(
    () => (latest ? buildThisMonthInventoryRows(monthBenefits, latest) : []),
    [latest, monthBenefits]
  )
  const tabCounts = tabBenefits.map((list) => {
    const tabRows = latest ? buildThisMonthInventoryRows(list, latest) : []
    return { listed: tabRows.length, inStock: tabRows.filter((r) => r.inStock).length }
  })
  const visibleRows = inStockOnly ? rows.filter((r) => r.inStock) : rows
  const inStockCount = rows.filter((r) => r.inStock).length

  // 期限ごとの直近アップロード日（どの銘柄のエントリにも同じ日付が入っている）
  const anyEntry = latest ? Object.values(latest).find((e) => e["14d"] || e.unlimited) : undefined
  const date14d = anyEntry?.["14d"]?.snapshotDate
  const dateUnlimited = anyEntry?.unlimited?.snapshotDate

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>一般信用在庫</h1>
        <Link to="/admin" className={styles.backLink}>
          ← 管理画面
        </Link>
      </div>

      <MarginInventoryUploadPanel onSaved={() => mutate()} />

      {latest && <MarginInventoryDiffPanel rows={rows} month={month} />}

      <div className={styles.tabs} role="tablist" aria-label="権利確定月">
        {MONTH_TAB_LABELS.map((label, offset) => {
          const m = ((currentMonth - 1 + offset) % 12) + 1
          return (
            <button
              key={label}
              type="button"
              role="tab"
              aria-selected={offset === monthOffset}
              className={offset === monthOffset ? styles.tabActive : styles.tab}
              onClick={() => setMonthOffset(offset)}
            >
              <span className={styles.tabLabel}>{label}</span>
              <span className={styles.tabMonth}>{m}月</span>
              {latest && (
                <span className={styles.tabCount}>
                  在庫あり {tabCounts[offset]?.inStock} / {tabCounts[offset]?.listed}
                </span>
              )}
            </button>
          )
        })}
      </div>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{month}月権利確定の優待銘柄の在庫</h2>
        <p className={styles.meta}>
          直近アップロード: 14日 {formatShortDate(date14d)} / 無期限{" "}
          {formatShortDate(dateUnlimited)}
          {latest && ` ・ ${rows.length}銘柄（在庫あり ${inStockCount}）`}
        </p>

        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={inStockOnly}
            onChange={(e) => setInStockOnly(e.target.checked)}
          />
          在庫ありのみ表示
        </label>

        <div className={styles.loanRate}>
          <span className={styles.loanRateLabel}>証券担保ローン金利（年率）</span>
          <div className={styles.loanRateOptions}>
            {LOAN_RATE_TIERS.map((t) => (
              <button
                key={t.rate}
                type="button"
                aria-pressed={t.rate === loanRate}
                className={t.rate === loanRate ? styles.loanRateActive : styles.loanRateButton}
                onClick={() => {
                  setLoanRate(t.rate)
                  try {
                    localStorage.setItem(LOAN_RATE_KEY, String(t.rate))
                  } catch {
                    // 保存できなくても選択自体は有効
                  }
                }}
              >
                {(t.rate * 100).toFixed(3)}%<span className={styles.loanRateSub}>{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        {(benefitsError || latestError) && (
          <p className={styles.error}>
            読み込みに失敗しました: {String((latestError ?? benefitsError)?.message ?? "")}
          </p>
        )}
        {!benefits || (tabCodes.length > 0 && !latest && !latestError) ? (
          <p className={styles.notice}>読み込み中...</p>
        ) : visibleRows.length === 0 ? (
          <p className={styles.notice}>該当する銘柄はありません</p>
        ) : (
          <ul className={styles.list}>
            {visibleRows.map((row) => (
              <MarginInventoryRowCard
                key={row.code}
                row={row}
                market={market?.[row.code]}
                today={today}
                loanRate={loanRate}
                overrides={valueOverrides?.[row.code]}
                onSaveOverrides={(values) => saveBenefitValues(row.code, values)}
                onCrawl={() => crawlCompany(row.code)}
                memo={memos?.[row.code] ?? ""}
                onSaveMemo={(memo) => saveMemo(row.code, memo)}
              />
            ))}
          </ul>
        )}
        <p className={styles.legend}>
          「—」はその期限の直近リストに載っていない銘柄。↑↓は直近3回の在庫変化（日付・増減数・変化率）。在庫数をタップで履歴の表とグラフ。クロス費用は売建側の貸株料と証券担保ローン利息の概算（現在値ベース。手数料・配当調整金は含まない。貸株料:
          一般14日3.9%・一般無期限1.1%、証券担保ローンは上で選んだ金利）。
        </p>
      </section>
    </div>
  )
}
